'use strict';
import { UserRoles } from './constants/UserRoles';
import Endpoints from './Endpoints.mjs';

/**
 * Configuration module
 * Write any (default) configuration here
 * @type {{}}
 */

type Config = {
    [key: string]: any;
};

const AppConfig: Config = {
    portalName: 'participant-portal',
    targetEnv: `${process.env.TARGET_ENV}`,
    routes: {
        defaultRoute: '/portal',
        loginRoute: '/portal/login',
        forgotPassword: '/portal/forgot-password',
        practiceSite: '/portal/practice',
        practiceSiteQuestionnairePrefix: '/portal/questionnaire-practice',
        practiceSiteEducationLevel: '/portal/practice/level',
        participations: '/portal/participations',
        questionnaire: '/portal/questionnaire', // appended by ps slug
        questionnaireSession: '/portal/questionnaire-session',
        questionnaires: '/portal/questionnaires', // appended by test handle and ps slug
        amendDetails: '/portal/amend_details', // appended by ps slug
        assessmentreport: '/portal/assessmentreport', // base url for reports
        profile: '/portal/profile',
        featureToggles: '/portal/featureToggles',
        reports: '/portal/reports',
        selfRegistration: '/portal/self-registration',
        magicLink: '/portal/magic-link',
        groupReport: '/portal/group-report', // appended by group report id
    },
    logger: {
        application: 'participant-portal',
        devModeBaseUrl: 'http://localhost:19270',
        listenerUrl: 'https://listener-eu.logz.io',
        devMode: false,
        baseUrl: `${process.env.NEON_API_BASE_URL}`,
        fetchTokenUrl: `${process.env.NEON_API_BASE_URL}/api/v1/logzio/key`
    },
    sentry: {
        dsn: `${process.env.SENTRY_DSN}`,
        environments: ['staging', 'development', 'production'],
        release: process.env.SENTRY_RELEASE,
        sampleRate: process.env.TARGET_ENV === 'production' ? 0.01 : 0.05,
        replayRate: process.env.TARGET_ENV === 'production' ? 0.01 : 0.1,
        developDsn: 'https://b4ab566b94f442f2b04e0bbfa13b69b7@o1351458.ingest.sentry.io/6634539'
    },
    api: {
        neon: {
            baseUrl: `${process.env.NEON_API_BASE_URL}`, // default is dev api, which is set in webpack.config.js
            endpoints: Endpoints,
            urlEncodeParams: true,
            skipPrefixIndexParams: true,
            requestFailedMessage: 'An error occurred while processing your request.',

            // allow to use cross-domain cookies for authentication
            // https://developer.mozilla.org/en-US/docs/Web/API/Request/credentials
            credentials: 'include',
            logoutRedirect: '/portal/login',
            maintenanceRedirect: '/portal/maintenance',
            loginRedirect: '/portal/login',
            loggingExclusions: {

                // endpoints of api errors should not be logged at all
                endpoints: [],

                // header fields from the api call that failed that should be excluded from logging
                headers: [],

                // fields from the post body of an api error that should be excluded from logging
                postBody: [
                    'password'
                ],

                // fields from the response body of an api error that should be excluded from logging
                responseBody: []
            }
        }
    },
    report: {
        textSaveInterval: 3000
    },
    authenticator: {
        neon: {
            loginRedirect: '/portal/login'
        },
        cognito: {
            userPoolId: `${process.env.COGNITO_USER_POOL_ID}`, //  default is dev, which is set in webpack.config.js
            appClientId: `${process.env.COGNITO_APP_CLIENT_ID}`
        },
        entra: {
            loginRedirect: '/portal/login'
        }
    },
    authoriser: {
        neon: {
            pwa: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS
                ]
            },
            participations: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                    UserRoles.ROLE_ASSESSORS,
                    UserRoles.ROLE_PARTICIPANT
                ]
            },
            questionnaires: {
                route: [
                    UserRoles.ROLE_PARTICIPANT
                ]
            },
            questionnairesV2: {
                route: [
                    UserRoles.ROLE_PARTICIPANT
                ]
            },
            questionnaireV2: {
                route: [
                    UserRoles.ROLE_PARTICIPANT
                ]
            },
            questionnaire: {
                route: [
                    UserRoles.ROLE_PARTICIPANT
                ]
            },
            amendDetails: {
                route: [
                    UserRoles.ROLE_PARTICIPANT
                ]
            },
            capacityTest: {
                route: [
                    UserRoles.ROLE_PARTICIPANT
                ]
            },
            loginRedirect: '/portal/login',
            assessmentReport: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_PARTICIPANT
                ],
                printAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CLIENT_CENTER
                ],
                writeAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_PARTICIPANT
                ],
                moveParticipantToPhaseAction: [
                    UserRoles.ROLE_PARTICIPANT
                ]
            },
            profile: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_PARTICIPANT
                ]
            },
            reports: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_PARTICIPANT
                ]
            },
            featureToggles: {
                featureToggleAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS
                ],
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS
                ]
            },
            groupReportPage: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_PARTICIPANT
                ]
            },

        }
    },
    global: {
        alertTimeout: 5000,
        assessmentReportScorePollingTimeout: 30000,
        forgotPasswordUrl: '/portal/forgot-password'
    },
    languages: {
        supported: [
            'nl_NL',
            'en_GB',
            'de_DE',
            'fr_FR'
        ],
        defaultLanguage: 'nl_NL',
        mapped: {
            nl: 'nl_NL',
            en: 'en_GB',
            de: 'de_DE',
            fr: 'fr_FR',
            'nl-nl': 'nl_NL',
            'nl_nl': 'nl_NL', // eslint-disable-line
            'en-gb': 'en_GB',
            'en_gb': 'en_GB', // eslint-disable-line
            'en-us': 'en_GB',
            'en_us': 'en_GB' // eslint-disable-line
        }
    }
};

export default AppConfig;
